import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeToggleProps {
  className?: string;
  position?: 'fixed' | 'relative';
}

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '', position = 'relative' }) => {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className={`${position === 'fixed' ? 'fixed z-50 top-10 right-6' : ''} ${className}`}>
      <button
        onClick={toggleTheme}
        className={`
          relative inline-flex items-center justify-center w-14 h-8 rounded-full transition-all duration-300 ease-in-out
          ${theme === 'dark' 
            ? 'bg-gradient-to-r from-slate-700 to-slate-800 shadow-lg' 
            : 'bg-gradient-to-r from-blue-400 to-blue-500 shadow-lg shadow-blue-200'
          }
          hover:scale-105 hover:shadow-xl
          ${theme === 'dark' ? 'hover:shadow-slate-500/25' : 'hover:shadow-blue-300/50'}
          focus:outline-none focus:ring-4 focus:ring-blue-300/30
          group
        `}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {/* Track */}
        <div className="absolute rounded-full inset-1">
          <div
            className={`
              absolute top-0 left-0 w-6 h-6 rounded-full transition-all duration-300 ease-in-out transform
              ${theme === 'dark' 
                ? 'translate-x-0 bg-gradient-to-br from-slate-200 to-white shadow-md' 
                : 'translate-x-6 bg-gradient-to-br from-yellow-200 to-yellow-400 shadow-md shadow-yellow-300/50'
              }
              group-hover:shadow-lg
            `}
          >
            {/* Icon */}
            <div className="flex items-center justify-center w-full h-full">
              {theme === 'dark' ? (
                <Moon className="w-3 h-3 text-slate-600" />
              ) : (
                <Sun className="w-3 h-3 text-yellow-600" />
              )}
            </div>
          </div>
        </div>

        {/* Background Icons */}
        <div className="absolute inset-0 flex items-center justify-between px-2">
          <Moon 
            className={`w-3 h-3 transition-opacity duration-300 ${
              theme === 'dark' ? 'opacity-0' : 'opacity-60 text-white'
            }`} 
          />
          <Sun 
            className={`w-3 h-3 transition-opacity duration-300 ${
              theme === 'dark' ? 'opacity-60 text-slate-300' : 'opacity-0'
            }`} 
          />
        </div>

        {/* Glow Effect */}
        <div 
          className={`
            absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300
            ${theme === 'dark' 
              ? 'bg-gradient-to-r from-slate-400/20 to-slate-500/20' 
              : 'bg-gradient-to-r from-blue-300/30 to-blue-400/30'
            }
          `}
        />
      </button>
    </div>
  );
};

export default ThemeToggle;
